import { DefaultModality } from 'amazon-chime-sdk-js';
import { Randomizer } from 'frontend/utils/number-array';
import { TrackedMap } from 'tracked-built-ins';
import { action } from '@ember/object';
import { reads } from 'macro-decorators';
import { tracked } from '@glimmer/tracking';
import Service, { service } from '@ember/service';
import classic from 'ember-classic-decorator';
import moment from 'moment-timezone';

@classic
export default class ChimeRosterService extends Service {
  @service('chime.meeting-manager') meetingManager;
  @service persistentProperties;
  @service session;
  @service soundNotification;
  @service uiElements;

  @tracked activeSpeakerId = null;
  roster = new TrackedMap();
  uniqRandom = new Randomizer(10);
  #rosterChangeListeners = [];

  @reads('meetingManager.localAttendeeId') localAttendeeId;
  @reads('session.firstParticipantJoinTime') firstParticipantJoinTime;
  @reads('uiElements.isGridView') isGridView;

  init() {
    super.init(...arguments);
    this.meetingManager.subscribeToAudioVideo(this.onAudioVideoChange);
  }

  get remoteAttendeeIds() {
    return this.roster.keys();
  }

  get size() {
    return this.roster.size;
  }

  @action
  async onRosterUpdate(chimeAttendeeId, present, externalUserId) {
    if (!present) {
      return this.handleAttendeeLeave(chimeAttendeeId);
    }

    let attendeeId = new DefaultModality(chimeAttendeeId).base();

    if (
      this.roster.has(chimeAttendeeId) ||
      attendeeId !== chimeAttendeeId ||
      this.localAttendeeId === attendeeId
    )
      return;

    this.soundNotification.playJoinSound();

    if (!this.firstParticipantJoinTime) {
      this.session.setFirstParticipantJoinTime(moment());
    }

    this.session.shareUserData(externalUserId);

    let attendee = { chimeAttendeeId, connected: moment() };

    if (externalUserId) {
      attendee.externalUserId = externalUserId;
      attendee.logoColor = this.uniqRandom.getItem(this.size + 1).toString();
    }

    this.roster.set(attendeeId, attendee);
    this.#publishRosterChanges(chimeAttendeeId, true);
  }

  setAttendeeUserData(externalUserId, userData) {
    let [attendeeId, attendeeData] = [...this.roster].find(
      ([_, data]) => data.externalUserId === externalUserId
    );

    this.roster.set(attendeeId, { ...attendeeData, ...userData });
  }

  @action
  onAudioVideoChange(audioVideo) {
    if (!audioVideo) {
      this.activeSpeakerId = null;
      return this.clear();
    }

    let { DefaultActiveSpeakerPolicy } = this.meetingManager.chimeSdk;
    let activeSpeakerPolicy = new DefaultActiveSpeakerPolicy();
    let activeSpeakerListener = ([activeSpeakerId]) => {
      if (activeSpeakerId === this.localAttendeeId || activeSpeakerId === this.activeSpeakerId)
        return;

      this.activeSpeakerId = activeSpeakerId;
    };
    audioVideo.realtimeSubscribeToAttendeeIdPresence(this.onRosterUpdate);
    audioVideo.subscribeToActiveSpeakerDetector(activeSpeakerPolicy, activeSpeakerListener);
  }

  handleAttendeeLeave(chimeAttendeeId) {
    this.soundNotification.playLeaveSound();
    this.roster.delete(chimeAttendeeId);

    if (this.activeSpeakerId === chimeAttendeeId) {
      this.activeSpeakerId = null;
    }

    if (this.size || !this.isGridView) return;

    this.uiElements.toggleGridView();
    this.#publishRosterChanges(chimeAttendeeId, false);
  }

  clear() {
    this.roster.clear();
  }

  getAttendee(attendeeId) {
    return this.roster.get(attendeeId);
  }

  subscribeToRosterChanges(callback) {
    this.#rosterChangeListeners.push(callback);
  }

  unsubscribeFromRosterChanges(callback) {
    this.#rosterChangeListeners = this.#rosterChangeListeners.filter(cb => cb !== callback);
  }

  #publishRosterChanges(attendeeId, isPresent) {
    this.#rosterChangeListeners.forEach(callback => callback({ attendeeId, isPresent }));
  }
}
